import axios from "axios";
import axiosInstance from "./axiosInstance";
import useSWR from "swr";
import { API } from "../constant";

// Public (no-auth) axios instance for magic-link confirmation page.
// Defined at module scope; intentionally bypasses the JWT interceptor.
const publicAxios = axios.create({ baseURL: API });

// kind constants
export const KIND_UTANG = 1;
export const KIND_PIUTANG = 2;

// ── hooks ──────────────────────────────────────────────

export const useDebtSummary = () => {
  const { data, mutate } = useSWR("/debts/summary");
  return {
    summary: data || {
      totalUtangOutstanding: 0,
      totalPiutangOutstanding: 0,
      netPosition: 0,
      openUtangCount: 0,
      openPiutangCount: 0,
      overdueCount: 0,
    },
    isLoading: data === undefined,
    refresh: mutate,
  };
};

export const useDebts = ({ kind, status, partyId } = {}) => {
  const params = [];
  if (kind != null) params.push("kind=" + kind);
  if (status) params.push("status=" + encodeURIComponent(status));
  if (partyId) params.push("partyId=" + encodeURIComponent(partyId));
  const qs = params.length ? "?" + params.join("&") : "";
  const { data, mutate, error } = useSWR("/debts" + qs);
  return {
    debts: data || [],
    isLoading: data === undefined,
    error,
    refresh: mutate,
  };
};

export const useDebt = (id) => {
  const { data, mutate, error } = useSWR(id ? "/debts/" + id : null);
  return {
    debt: data,
    isLoading: data === undefined,
    error,
    refresh: mutate,
  };
};

export const useDebtParties = () => {
  const { data, mutate } = useSWR("/debt-parties");
  return {
    parties: data || [],
    isLoading: data === undefined,
    refresh: mutate,
  };
};

// ── mutations ──────────────────────────────────────────

export async function debtCreate(body) {
  return await axiosInstance.post("/debts", body);
}

export async function debtUpdate(id, body) {
  return await axiosInstance.put("/debts/" + id, body);
}

export async function debtDelete(id) {
  return await axiosInstance.delete("/debts/" + id);
}

export async function debtClose(id) {
  return await axiosInstance.post("/debts/" + id + "/close", {});
}

export async function debtAddPayment(id, body) {
  return await axiosInstance.post("/debts/" + id + "/payments", body);
}

export async function debtDeletePayment(debtId, paymentId) {
  return await axiosInstance.delete(
    "/debts/" + debtId + "/payments/" + paymentId
  );
}

export async function debtPartyCreate(body) {
  return await axiosInstance.post("/debt-parties", body);
}

export async function debtSendConfirmation(id) {
  return await axiosInstance.post("/debts/" + id + "/confirmation/send", {});
}

export async function debtUpdateAmount(id, body) {
  return await axiosInstance.put("/debts/" + id + "/amount", body);
}

export async function debtAcknowledgeDispute(id, body) {
  return await axiosInstance.post("/debts/" + id + "/dispute/acknowledge", body);
}

export async function debtEditAndResend(id, body) {
  return await axiosInstance.post(
    "/debts/" + id + "/dispute/edit-and-resend",
    body
  );
}

// ── Party detail + share ───────────────────────────────

export const usePartyDetail = (id) => {
  const { data, mutate, error } = useSWR(id ? "/debt-parties/" + id : null);
  return {
    party: data,
    isLoading: data === undefined,
    error,
    refresh: mutate,
  };
};

export async function partyShareRegenerate(id) {
  return await axiosInstance.post("/debt-parties/" + id + "/share/regenerate", {});
}

export async function partyShareSend(id) {
  return await axiosInstance.post("/debt-parties/" + id + "/share/send", {});
}

export async function partyShareRevoke(id) {
  return await axiosInstance.delete("/debt-parties/" + id + "/share");
}

export async function publicPartyGet(token) {
  return await publicAxios.get("/public/parties/" + token);
}

// ── Public (no-auth) confirmation API ───────────────────

export async function publicConfirmationGet(token) {
  return await publicAxios.get("/public/debts/confirm/" + token);
}

export async function publicConfirmationAct(token, body) {
  return await publicAxios.post("/public/debts/confirm/" + token, body);
}
